import isAfter from 'date-fns/isAfter'
import isBefore from 'date-fns/isBefore'
import isWithinInterval from 'date-fns/isWithinInterval'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import startOfYear from 'date-fns/startOfYear'
import endOfDay from 'date-fns/endOfDay'
import addYears from 'date-fns/addYears'
import addMonths from 'date-fns/addMonths'
import addDays from 'date-fns/addDays'
import startOfWeek from 'date-fns/startOfWeek'
import { MY_PGA_COM_HOST, APP_ENVIRONMENT } from 'env'

export const rangeFromDates = dates =>
  dates.reduce((acc, date) => {
    if (!acc) {
      return { startDate: date, endDate: date }
    }

    const { startDate, endDate } = acc

    if (!startDate || isBefore(date, startDate)) {
      return { endDate, startDate: date }
    }

    if (!endDate || isAfter(date, endDate)) {
      return { startDate, endDate: date }
    }

    return acc
  }, null)

export const getAlgoliaTimestamp = (date = new Date()) =>
  Math.floor(date / 1000)

const createOption = ({ formatLabel, formatValue }) => value => ({
  label: format(value, formatLabel),
  value: format(value, formatValue),
})

const dateSteps = {
  YEAR: addYears,
  MONTHS: addMonths,
  DAYS: addDays,
}

export const getDateOptions = (
  amount,
  {
    step = 'YEAR',
    formatLabel = 'yyyy',
    formatValue = 'yyyy',
    start = new Date(),
  } = {},
) => {
  const startDate = startOfYear(start)
  const years = Array.from({ length: amount }).map((_, i) =>
    dateSteps[step](startDate, i),
  )

  return years.map(createOption({ formatLabel, formatValue }))
}

export const canCreateCurrentYearProgram = () => {
  const isDev =
    MY_PGA_COM_HOST.includes('.dev.') || MY_PGA_COM_HOST.includes('.qa.')
  const isFallSeasonAvailable = new Date().getMonth() < 9
  return isDev || isFallSeasonAvailable
}

export const parseSeasonDate = value => {
  if (!value) return null
  if (value instanceof Date) return value
  return parseISO(value)
}

export const openSeasonYears = (seasons = [], today = new Date()) =>
  seasons
    .filter(season => {
      const start = parseSeasonDate(season.openDate)
      const end = parseSeasonDate(season.closeDate)
      if (!start || !end) return false
      return isWithinInterval(today, { start, end: endOfDay(end) })
    })
    .map(season => Number(season.year))
    .sort((a, b) => a - b)

export const seasonForYear = (seasons = [], year) =>
  seasons.find(season => Number(season.year) === Number(year))

const parseLocalCalendarDate = value => {
  if (!value) return null
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value))
  if (!match) return null
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

export const scheduleEventMinDate = ({
  year,
  playerRegistrationWindows = [],
} = {}) => {
  const yearNum = Number(year)
  if (!yearNum) return null

  const yearStart = new Date(yearNum, 0, 1)
  const openDate = parseLocalCalendarDate(
    seasonForYear(playerRegistrationWindows, year)?.openDate,
  )
  if (openDate && openDate > yearStart) return openDate
  return yearStart
}

export const defaultJRLOpenDate = year => {
  const yearSet = Number(year || new Date().getFullYear())
  return new Date(yearSet, 0, 5)
}

export const regDateIntervalForSeason = (season, year) => {
  if (season?.openDate && season?.closeDate) {
    return {
      start: parseSeasonDate(season.openDate),
      end: endOfDay(parseSeasonDate(season.closeDate)),
    }
  }

  return {
    start: defaultJRLOpenDate(year),
    end: endOfDay(new Date(Number(year), 11, 12)),
  }
}

export const defaultYearFilter = (seasons, today = new Date()) => {
  if (seasons?.length) {
    const openYears = openSeasonYears(seasons, today)
    if (openYears.length) return Math.max(...openYears)
  }

  const currentYear = Number(today.getFullYear())
  const shouldShowNextYearPrograms = today >= new Date(currentYear, 10, 1)
  return shouldShowNextYearPrograms ? currentYear + 1 : currentYear
}

/** Past years stay on a fixed sliding window; open coach-creation years extend the max. */
export const getProgramYearFilterOptions = ({
  coachCreationWindows = [],
  today = new Date(),
} = {}) => {
  const startYear = 2021
  const currentYear = today.getFullYear()
  const openYears = openSeasonYears(coachCreationWindows, today)
  const maxYear = Math.max(
    currentYear,
    defaultYearFilter(coachCreationWindows, today),
    ...(openYears.length ? openYears : []),
  )
  const minYear = Math.max(startYear, maxYear - 5)

  const years = []
  for (let y = minYear; y <= maxYear; y++) {
    years.push({ label: String(y), value: y })
  }
  years.push({ label: 'All', value: null })
  return years
}

export const defaultJRLMinDeliveryDate = year => {
  // 2 weeks after open date
  const openDate = defaultJRLOpenDate(year)
  return addDays(openDate, 14)
}

export const deliveryDateIntervalForSeason = (season, year) => {
  if (season?.openDate && season?.closeDate) {
    return {
      start: parseSeasonDate(season.openDate),
      end: endOfDay(parseSeasonDate(season.closeDate)),
    }
  }

  return {
    start: defaultJRLMinDeliveryDate(year),
    end: endOfDay(new Date(Number(year), 11, 30)),
  }
}

export const winterSeasonCutoffDate = () => {
  // February 28th of current year
  const currentYear = new Date().getFullYear()
  return new Date(Date.UTC(currentYear, 1, 28, 23, 59, 59))
}

export const champSeasonCutoffDate = () => {
  // August 26th of current year
  const currentYear = new Date().getFullYear()
  return new Date(Date.UTC(currentYear, 7, 26, 23, 59, 59))
}

export const nextYearProgramRegistrationOpenDate = () => {
  const currentYear = new Date().getFullYear()

  // Move up the date for dev testing
  if (APP_ENVIRONMENT === 'development' || APP_ENVIRONMENT === 'dev') {
    return new Date(currentYear, 9, 1)
  }

  // First Monday in November
  const nov1st = new Date(currentYear, 10, 1)
  const firstMonday = startOfWeek(nov1st, { weekStartsOn: 1 })
  return nov1st > firstMonday ? addDays(firstMonday, 7) : firstMonday
}

// Second Friday of December (UTC EOD) for given year
export const teamKitOrderCutoffDate = (year = new Date().getFullYear()) => {
  const decFirstUtc = new Date(Date.UTC(year, 11, 1, 0, 0, 0))
  const dayOfWeek = decFirstUtc.getUTCDay()
  const friday = 5
  const daysUntilFirstFriday = (friday - dayOfWeek + 7) % 7
  const firstFridayDayOfMonth = 1 + daysUntilFirstFriday
  const secondFridayDayOfMonth = firstFridayDayOfMonth + 7
  return new Date(Date.UTC(year, 11, secondFridayDayOfMonth, 23, 59, 59))
}

export const isPastTeamKitOrderCutoffForProgram = programYear => {
  const year = Number(programYear)
  if (!year) return false
  return new Date() > teamKitOrderCutoffDate(year)
}
